import { Link } from 'react-router-dom';
import { MapPin, Calendar, Briefcase, Clock, CheckCircle2, XCircle, AlertCircle, ArrowRight, Building2, Bookmark, ExternalLink } from 'lucide-react';
import { useMyAppliedJobs } from '@/hooks/useOffer';
import { useAuthStore } from '@/store/authStore';
import { cn, formatSalaryRange } from '@/lib/utils';
import dayjs from '@/lib/dayjs';

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, any> = {
    accepted: { label: 'Nhà tuyển dụng đã phản hồi', icon: <CheckCircle2 size={12} />, bg: '#dcfce7', color: '#16a34a' },
    rejected: { label: 'Bị từ chối', icon: <XCircle size={12} />, bg: '#fee2e2', color: '#dc2626' },
    pending: { label: 'Đang chờ xử lý', icon: <AlertCircle size={12} />, bg: '#fef9c3', color: '#ca8a04' },
  };
  const s = map[status] ?? map.pending;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold" style={{ background: s.bg, color: s.color }}>
      {s.icon} {s.label}
    </span>
  );
}

function StageBadge({ stage }: { stage: string }) {
  const map: Record<string, any> = {
    NEW: { label: 'Mới ứng tuyển', color: '#64748b', bg: '#f1f5f9' },
    CONTACTED: { label: 'Đang liên hệ', color: '#0284c7', bg: '#e0f2fe' },
    INTERVIEW: { label: 'Phỏng vấn', color: '#9333ea', bg: '#f3e8ff' },
    OFFER: { label: 'Đã trúng tuyển', color: '#16a34a', bg: '#dcfce7' },
    REJECTED: { label: 'Không phù hợp', color: '#dc2626', bg: '#fee2e2' },
  };
  const s = map[stage] ?? map.NEW;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border" style={{ background: s.bg, color: s.color, borderColor: s.color + '40' }}>
      Giai đoạn: {s.label}
    </span>
  );
}

function OfferCard({ item }: { item: any }) {
  const job = item.entityData || {};
  const companyName = job.company?.name || job.companyName || 'Công ty ẩn danh';

  return (
    <div className="fnb-card p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div>
          <Link to={`/viec-lam/${job.slug || job.id || item.entityId}`} className="font-bold text-base hover:text-[#f97316] transition-colors line-clamp-1" style={{ color: 'var(--text-1)' }}>
            {job.title || 'Vị trí Ẩn danh'} <ExternalLink size={14} className="inline-block ml-1 opacity-50" />
          </Link>
          <div className="flex items-center gap-1.5 text-sm font-medium mt-1" style={{ color: 'var(--text-2)' }}>
            <Building2 size={14} className="text-gray-400" /> {companyName}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <StatusBadge status={item.status} />
          {item.stage && <StageBadge stage={item.stage} />}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs" style={{ color: 'var(--text-3)' }}>
        <span className="flex items-center gap-1">
          <MapPin size={12} /> {job.workingLocation || 'Chưa cập nhật'}
        </span>
        <span className="flex items-center gap-1">
          <Briefcase size={12} /> {job.jobType || 'Toàn thời gian'}
        </span>
        <span className="flex items-center gap-1 font-semibold" style={{ color: 'var(--brand)' }}>
          💰 {formatSalaryRange(job.salaryMin, job.salaryMax)} {job.currencyUnit || 'VND'}
        </span>
      </div>

      <div className="pt-3 mt-1 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-c)' }}>
        <div className="flex items-center gap-1 text-xs font-medium" style={{ color: 'var(--text-3)' }}>
          <Calendar size={12} /> Ứng tuyển: {dayjs(item.createdAt).format('DD/MM/YYYY HH:mm')}
        </div>
        <Link to={`/viec-lam/${job.slug || job.id || item.entityId}`} className="text-xs font-bold text-[#f97316] hover:underline">
          Xem chi tiết công việc
        </Link>
      </div>
    </div>
  );
}

export default function AppliedJobsPage() {
  const { user } = useAuthStore();
  const { data, isLoading } = useMyAppliedJobs(user?.id || '', 1);
  const offers = Array.isArray(data) ? data : data?.data ?? [];
  const total = Array.isArray(data) ? data.length : data?.total ?? 0;

  const stats = {
    total: total,
    accepted: offers.filter((p: any) => p.status === 'accepted').length,
    pending: offers.filter((p: any) => p.status === 'pending').length,
    rejected: offers.filter((p: any) => p.status === 'rejected').length,
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="section-label">Việc làm</div>
        <div className="flex items-end justify-between flex-wrap gap-2">
          <div>
            <h1 className="section-title mb-1">Việc đã ứng tuyển</h1>
            <p className="text-sm" style={{ color: 'var(--text-3)' }}>
              Theo dõi trạng thái các hồ sơ bạn đã nộp
            </p>
          </div>
          <Link
            to="/viec-lam"
            className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl border hover:border-[#f97316] hover:text-[#f97316] transition-colors"
            style={{ borderColor: 'var(--border-c)', color: 'var(--text-2)' }}
          >
            <Briefcase size={14} /> Tìm thêm việc làm
          </Link>
        </div>
      </div>

      {/* Stats */}
      {offers.length > 0 && (
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Đã ứng tuyển', value: stats.total, color: 'var(--text-1)', bg: 'var(--surface-1)' },
            { label: 'Đã phản hồi', value: stats.accepted, color: '#16a34a', bg: '#dcfce7' },
            { label: 'Đang chờ', value: stats.pending, color: '#ca8a04', bg: '#fef9c3' },
            { label: 'Từ chối', value: stats.rejected, color: '#dc2626', bg: '#fee2e2' },
          ].map((s) => (
            <div key={s.label} className="rounded-xl p-4 text-center" style={{ background: s.bg, border: '1px solid var(--border-c)' }}>
              <p className="text-2xl font-black" style={{ color: s.color }}>
                {s.value}
              </p>
              <p className="text-xs mt-1 font-bold" style={{ color: 'var(--text-3)' }}>
                {s.label}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-10 h-10 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--brand-dim)', borderTopColor: 'var(--brand)' }} />
        </div>
      ) : offers.length === 0 ? (
        <div className="text-center py-24 rounded-2xl border" style={{ borderColor: 'var(--border-c)', background: 'var(--surface-0)' }}>
          <p className="text-5xl mb-4">💼</p>
          <p className="font-bold text-lg mb-2" style={{ color: 'var(--text-1)' }}>
            Bạn chưa ứng tuyển vị trí nào
          </p>
          <p className="text-sm mb-6" style={{ color: 'var(--text-3)' }}>
            Khám phá hàng ngàn việc làm F&B đang tuyển
          </p>
          <Link to="/viec-lam" className="btn-brand inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold">
            Tìm việc ngay <ArrowRight size={15} />
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {offers.map((item: any, i: number) => (
            <div key={item.id} className="animate-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <OfferCard item={item} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
